import { formatDay, formatRanges, formatShort, type IsoDate } from './dates';
import { formatInr, upiLink } from './payments';
import { tiffinsLabel, type TiffinLine } from './meals';
import { daysLabel } from './weekdays';
import { mealWords, mealsOf, type Meal, type PayMethod, type Slot } from './types';

export type MessageType = 'renewal' | 'payment' | 'status';

export interface MessageContext {
  kitchenName: string;
  upiId?: string | null;
  customerName: string;
  planName: string;
  endDate: IsoDate;
  mealsLeft: number;
  due: number;
  price: number;
  pausedDays: IsoDate[];
  /** The plan's own weekdays, when it has them ("Mon, Tue, Wed"). */
  days?: readonly number[];
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/*
 * WhatsApp formatting: *bold*, _italic_, "- " bullets and "1. " numbered lists render in the chat.
 * Messages lead with a short title, keep one fact per line, and end by saying what to reply,
 * so a customer can answer with one word and staff can read the answer at a glance.
 */
const bold = (s: string | number): string => `*${s}*`;
const signOff = (kitchenName: string): string => `_— ${kitchenName}_`;
const mealCount = (n: number): string => `${n} ${n === 1 ? 'meal' : 'meals'}`;
const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** The UPI ID stays plain (no bold) so a long-press copies it cleanly. */
function payBlock(ctx: MessageContext, amount: number): string[] {
  if (!ctx.upiId || amount <= 0) return [];
  const link = upiLink({ vpa: ctx.upiId, name: ctx.kitchenName, amount, note: `${ctx.planName}` });
  return ['', `💳 Pay ${bold(formatInr(amount))} by UPI`, `UPI ID: ${ctx.upiId}`, link];
}

/** WhatsApp text for the three reminders staff send. Plain text, no API needed. */
export function buildMessage(type: MessageType, ctx: MessageContext): string {
  const hi = `Hi ${firstName(ctx.customerName)} 👋`;
  switch (type) {
    case 'renewal':
      return [
        hi,
        '',
        `Your ${bold(ctx.planName)} plan at ${bold(ctx.kitchenName)} ends on ${bold(formatDay(ctx.endDate))}, with ${bold(mealCount(ctx.mealsLeft))} left.`,
        '',
        'Renew now to keep your meals coming without a break 🍱',
        ...payBlock(ctx, ctx.due > 0 ? ctx.due : ctx.price),
        '',
        `Reply ${bold('RENEW')} to continue.`,
      ].join('\n');
    case 'payment':
      return [
        hi,
        '',
        `A gentle reminder from ${bold(ctx.kitchenName)}: ${bold(formatInr(ctx.due))} is pending for your ${bold(ctx.planName)} plan.`,
        ...payBlock(ctx, ctx.due),
        '',
        `Reply ${bold('PAID')} once done. Thank you! 🙏`,
      ].join('\n');
    case 'status':
      return [
        hi,
        `Here's your plan update from ${bold(ctx.kitchenName)}:`,
        '',
        `🍱 ${bold(ctx.planName)}`,
        `- Meals left: ${bold(ctx.mealsLeft)}`,
        ...(ctx.days ? [`- Days: ${daysLabel(ctx.days)}`] : []),
        `- Ends on: ${bold(formatDay(ctx.endDate))}`,
        ...(ctx.pausedDays.length ? [`- Paused: ${formatRanges(ctx.pausedDays)}`] : []),
        ctx.due > 0 ? `- Balance due: ${bold(formatInr(ctx.due))}` : `- Payment: ${bold('Paid up')} ✅`,
        '',
        `Reply ${bold('PAUSE')} to skip a day or ${bold('RENEW')} to continue.`,
      ].join('\n');
  }
}

export function pauseConfirmation(opts: {
  kitchenName: string;
  customerName: string;
  days: IsoDate[];
  /** The meals paused. */
  meals: Slot;
  /** The plan's meals: when every one is paused the message just says "meals". */
  plan?: Slot;
  newEndDate: IsoDate;
}): string {
  const what = pausedWords(opts.meals, opts.plan);
  return [
    `⏸️ ${bold(`${capitalise(what)} paused`)}`,
    '',
    `Hi ${firstName(opts.customerName)}, your ${what} ${isAre(what)} paused on ${bold(formatRanges(opts.days))}.`,
    `📅 Your plan now ends on ${bold(formatDay(opts.newEndDate))}.`,
    '',
    signOff(opts.kitchenName),
  ].join('\n');
}

/** "meals" when the whole plan is paused that day, else the meals by name ("breakfast", "lunch and dinner"). */
function pausedWords(meals: Slot, plan?: Slot): string {
  if (plan && mealsOf(plan).length > 1 && mealsOf(meals).length === mealsOf(plan).length) return 'meals';
  return mealWords(meals);
}

/** "your lunch is", "your meals are", "your breakfast and lunch are". */
const isAre = (what: string): string => (what === 'meals' || what.includes(' and ') ? 'are' : 'is');

/** One message for a schedule change: days paused, days resumed, and the new end date. */
export function scheduleChangeMessage(opts: {
  kitchenName: string;
  customerName: string;
  paused: IsoDate[];
  resumed: IsoDate[];
  meals: Slot;
  plan?: Slot;
  newEndDate: IsoDate;
}): string {
  const what = pausedWords(opts.meals, opts.plan);
  const parts: string[] = [];
  if (opts.paused.length) parts.push(`your ${what} ${isAre(what)} paused on ${bold(formatRanges(opts.paused))}`);
  if (opts.resumed.length) parts.push(`your ${what} will be delivered again on ${bold(formatRanges(opts.resumed))}`);
  const title = opts.paused.length && opts.resumed.length
    ? `📅 ${bold('Plan updated')}`
    : opts.paused.length ? `⏸️ ${bold(`${capitalise(what)} paused`)}` : `▶️ ${bold(`${capitalise(what)} back on`)}`;
  return [
    title,
    '',
    `Hi ${firstName(opts.customerName)}, ${parts.join(' and ')}.`,
    `📅 Your plan now ends on ${bold(formatDay(opts.newEndDate))}.`,
    '',
    signOff(opts.kitchenName),
  ].join('\n');
}

/** "Hi Rohit, your dinner on *Mon 26 Oct* will come as lunch on *Tue 27 Oct* instead." and the end date. */
export function moveMessage(opts: {
  kitchenName: string;
  customerName: string;
  from: { day: IsoDate; meal: Meal };
  to: { day: IsoDate; meal: Meal };
  endDate: IsoDate;
}): string {
  const where = opts.from.meal === opts.to.meal ? `on ${bold(formatDay(opts.to.day))}` : `as ${opts.to.meal} on ${bold(formatDay(opts.to.day))}`;
  return [
    `🔁 ${bold('Meal moved')}`,
    '',
    `Hi ${firstName(opts.customerName)}, your ${opts.from.meal} on ${bold(formatDay(opts.from.day))} will come ${where} instead.`,
    `📅 Your plan ends on ${bold(formatDay(opts.endDate))}.`,
    '',
    signOff(opts.kitchenName),
  ].join('\n');
}

export function resumeConfirmation(opts: {
  kitchenName: string;
  customerName: string;
  days: IsoDate[];
  newEndDate: IsoDate;
}): string {
  return [
    `▶️ ${bold('Meals back on')}`,
    '',
    `Hi ${firstName(opts.customerName)}, your meals will be delivered again on ${bold(formatRanges(opts.days))}.`,
    `📅 Your plan now ends on ${bold(formatDay(opts.newEndDate))}.`,
    '',
    signOff(opts.kitchenName),
  ].join('\n');
}

export function receiptMessage(opts: {
  kitchenName: string;
  customerName: string;
  amount: number;
  method: PayMethod;
  paidOn: IsoDate;
  balance: number;
}): string {
  return [
    `✅ ${bold('Payment received')}`,
    '',
    `Hi ${firstName(opts.customerName)}, thank you! We received ${bold(formatInr(opts.amount))} by ${opts.method === 'upi' ? 'UPI' : 'cash'} on ${formatShort(opts.paidOn)}.`,
    opts.balance > 0 ? `Balance due: ${bold(formatInr(opts.balance))}` : `Your plan is ${bold('paid in full')} 🎉`,
    '',
    signOff(opts.kitchenName),
  ].join('\n');
}

export function welcomeMessage(opts: {
  kitchenName: string;
  customerName: string;
  planName: string;
  startDate: IsoDate;
  endDate: IsoDate;
  /** The plan's own weekdays, when it has them. */
  days?: readonly number[];
}): string {
  return [
    `🎉 ${bold(`Welcome to ${opts.kitchenName}, ${firstName(opts.customerName)}!`)}`,
    '',
    `Your ${bold(opts.planName)} plan is all set:`,
    ...(opts.days ? [`- Days: ${bold(daysLabel(opts.days))}`] : []),
    `- Starts: ${bold(formatDay(opts.startDate))}`,
    `- Ends: ${bold(formatDay(opts.endDate))}`,
    '',
    'Message us here any time to pause a day or renew 🍱',
  ].join('\n');
}

/** For delivery staff: one numbered list per area, so they can say "Indiranagar 3 is done". */
export function deliveryListMessage(opts: {
  kitchenName: string;
  day: IsoDate;
  meal: Meal;
  total: number;
  areas: Array<{ name: string; count: number; customers: Array<{ name: string; lastDay: boolean; tiffins: readonly TiffinLine[] }> }>;
}): string {
  const lines = [`🛵 ${bold(`${opts.kitchenName} · ${capitalise(opts.meal)}`)}`, `${formatDay(opts.day)} · ${bold(mealCount(opts.total))}`];
  for (const a of opts.areas) {
    lines.push('', `📍 ${bold(a.name)} (${a.count})`);
    a.customers.forEach((c, i) => {
      const what = tiffinsLabel(c.tiffins);
      lines.push(`${i + 1}. ${c.name}${what ? ` (${what})` : ''}${c.lastDay ? ' · _last day_' : ''}`);
    });
  }
  return lines.join('\n');
}

/** What staff send a customer after marking a delivery: delivered, not home, or could not deliver. */
export function deliveryUpdateMessage(opts: {
  kitchenName: string;
  customerName: string;
  meal: Meal;
  status: 'delivered' | 'not_home' | 'failed';
  day: IsoDate;
  today: IsoDate;
}): string {
  const hi = `Hi ${firstName(opts.customerName)}`;
  const meal = opts.meal;
  const when = opts.day === opts.today ? 'today' : `on ${bold(formatDay(opts.day))}`;
  const body =
    opts.status === 'delivered'
      ? [
          `🍱 ${bold(`${capitalise(meal)} delivered`)}`,
          '',
          opts.day === opts.today
            ? `${hi}, your ${meal} has been delivered. Enjoy your meal! 😊`
            : `${hi}, your ${meal} ${when} was delivered. Enjoy! 😊`,
        ]
      : opts.status === 'not_home'
        ? [
            `🛵 ${bold('We missed you')}`,
            '',
            `${hi}, we came by with your ${meal} ${when} but couldn't reach you. Reply here if we should leave it with someone or at the door next time.`,
          ]
        : [
            `🙏 ${bold(`${capitalise(meal)} not delivered`)}`,
            '',
            `${hi}, sorry, we couldn't deliver your ${meal} ${when}. It's added to the end of your plan, so you don't lose a meal.`,
          ];
  return [...body, '', signOff(opts.kitchenName)].join('\n');
}

/** After a renewal: the new dates, the money, and the new meals when they changed. */
export function renewalConfirmationMessage(opts: {
  kitchenName: string;
  customerName: string;
  planName: string;
  slot: Slot;
  previousSlot?: Slot | null;
  startDate: IsoDate;
  endDate: IsoDate;
  price: number;
  /** Everything paid towards the new plan so far, credit included. */
  paid?: number;
  method?: PayMethod;
  /** Carried over from the plan this one replaces. */
  creditApplied?: number;
}): string {
  const paid = opts.paid ?? 0;
  const credit = opts.creditApplied ?? 0;
  const due = Math.max(0, opts.price - paid);
  const meals = `${capitalise(mealWords(opts.slot))}${mealsOf(opts.slot).length === 1 ? ' only' : ''}`;
  const paidLine = [
    `- Paid: ${bold(formatInr(paid))}`,
    paid > credit && opts.method ? ` via ${opts.method === 'upi' ? 'UPI' : 'cash'}` : '',
    credit > 0 ? ` (${formatInr(credit)} carried over from your last plan)` : '',
  ].join('');
  return [
    `🎉 ${bold(`Renewed, ${firstName(opts.customerName)}!`)}`,
    '',
    opts.previousSlot && opts.previousSlot !== opts.slot
      ? `From ${bold(formatDay(opts.startDate))}, your meals switch to ${bold(meals)}.`
      : `Your ${bold(opts.planName)} continues from ${bold(formatDay(opts.startDate))}.`,
    `- Ends: ${bold(formatDay(opts.endDate))}`,
    `- Amount: ${bold(formatInr(opts.price))}`,
    ...(paid > 0 ? [paidLine] : []),
    ...(due > 0 ? [`- Still to pay: ${bold(formatInr(due))}`] : []),
    '',
    'See you then 🍱',
    '',
    signOff(opts.kitchenName),
  ].join('\n');
}
